import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import { QRCodeSVG } from "qrcode.react";
import { format, parseISO } from "date-fns";
import { RiFileCopyLine, RiCheckLine, RiShareForwardLine, RiRefreshLine, RiLinksLine } from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { colors } from "@styles/colors";
import api, { handleRequest } from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { IdentityContext } from "@utils/store/user/useUser";
import { appToast } from "@components/Toast";
import { buildInviteJoinUrl, buildInviteShareText } from "@utils/inviteLink";
import { track } from "@lib/tracker";
import { v2, SkinCard, SkinButton } from "@screen/Home/pages/GasShop/skin";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";

const HOW_TO_STEPS = [
  "在他的 App 點「+ 新增帳戶」",
  "選擇「加入既有的瓦斯帳戶」",
  "輸入這組 6 位數邀請碼（或直接掃描上方 QR Code）",
  "確認加入，由你核准後即可共同管理此帳戶",
];

interface MemberVM {
  customer_name: string;
  color?: string;
}

/** GET /app-api/customer/contexts/:id 的成員子集（補上權威成員清單與名稱） */
interface ContextDetailMember {
  customer_id: number;
  customer_name: string;
  role: string;
}
interface ContextDetailData {
  context_name?: string;
  members?: ContextDetailMember[];
}

/**
 * 邀請成員。
 * 1. 進頁先用 router state 的 ctx.members 即時渲染，再以 GET /customer/contexts/:id 補上權威成員清單。
 * 2. 「產生邀請碼」呼叫 POST /customer/contexts/:id/invite-code（限 owner），回傳 6 碼英數邀請碼與到期時間。
 * 3. 產生後顯示 QR Code（對方掃描 → 加入既有的瓦斯帳戶流程並自動帶碼）、可複製、可分享。
 */
export const InviteMember = () => {
  const navigate = useNavigate();
  const { contextId } = useParams();
  const location = useLocation();
  const { cusJwtToken } = useCusAuth();
  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });

  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [loading, setLoading] = useState(false);

  const isOwner = ctx?.role === "owner";
  const contextName = ctx?.label || ctx?.context_name;

  // 權威成員清單（API 補強），預設用 router state 帶入的。
  const [members, setMembers] = useState<MemberVM[]>(ctx?.members ?? []);

  // 進頁先查「目前仍在有效期的邀請碼」（owner 才查）：有就直接顯示，
  // 不必每次進頁都重產一組（後端 GET /contexts/:id/invite-code，過期回 null）。
  useEffect(() => {
    if (!cusJwtToken || !contextId || !isOwner) return;
    let active = true;
    api
      .get(`/app-api/customer/contexts/${contextId}/invite-code`, {
        headers: { Authorization: `Bearer ${cusJwtToken}` },
      })
      .then((res) => {
        if (!active) return;
        const data = (res.data?.data ?? null) as { invite_code?: string | null; expires_at?: string | null } | null;
        if (data?.invite_code) {
          setInviteCode(data.invite_code);
          setExpiresAt(data.expires_at ?? null);
        }
      })
      .catch(() => {
        // 靜默：查不到就維持「尚未產生邀請碼」，使用者可自行按產生
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, contextId, isOwner]);

  useEffect(() => {
    if (!cusJwtToken || !contextId) return;
    let active = true;
    api
      .get(`/app-api/customer/contexts/${contextId}`, { headers: { Authorization: `Bearer ${cusJwtToken}` } })
      .then((res) => {
        if (!active) return;
        const data = (res.data?.data ?? null) as ContextDetailData | null;
        if (data?.members) {
          setMembers(data.members.map((m) => ({ customer_name: m.customer_name })));
        }
      })
      .catch(() => {
        // 靜默：保留 router state 的既有顯示
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, contextId]);

  const generate = () => {
    if (loading || !contextId) return;
    if (!isOwner) {
      appToast.error("只有帳戶負責人可以產生邀請碼");
      return;
    }
    handleRequest<{ data: { invite_code: string; expires_at: string } }>({
      asyncRequest: () =>
        api.post(
          `/app-api/customer/contexts/${contextId}/invite-code`,
          { is_payer: false },
          { headers: { Authorization: `Bearer ${cusJwtToken}` } },
        ),
      successCallback: (res) => {
        const data = res.data?.data;
        if (!data?.invite_code) {
          appToast.error("產生邀請碼失敗，請稍後再試");
          return;
        }
        setInviteCode(data.invite_code);
        setExpiresAt(data.expires_at ?? null);
      },
      errorCallback: () => {
        appToast.error("產生邀請碼失敗，請稍後再試");
      },
      setLoading,
    });
  };

  const copy = () => {
    if (!inviteCode) return;
    navigator.clipboard?.writeText(inviteCode);
    setCopied(true);
    appToast.success("已複製邀請碼");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    if (!joinUrl) return;
    navigator.clipboard?.writeText(joinUrl);
    setCopiedLink(true);
    appToast.success("已複製邀請連結");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const share = async () => {
    if (!inviteCode) return;
    const text = buildInviteShareText(inviteCode, contextName);
    try {
      if (navigator.share) {
        // text 內已含加入網址，不再傳 url，避免分享時出現兩條相同網址
        await navigator.share({ title: "邀請你加入瓦斯帳戶", text });
        track("invite_member_share", { channel: "native_share" });
        return;
      }
      await navigator.clipboard?.writeText(text);
      track("invite_member_share", { channel: "clipboard_fallback" });
      appToast.success("已複製邀請連結");
    } catch {
      // 使用者取消分享：不提示
    }
  };

  const expiryLabel = (() => {
    if (!inviteCode) return "尚未產生邀請碼";
    if (!expiresAt) return "請儘速分享給對方";
    try {
      return `有效期限至 ${format(parseISO(expiresAt), "HH:mm")}`;
    } catch {
      return "請儘速分享給對方";
    }
  })();

  const cells = (inviteCode ?? "------").split("");
  const joinUrl = inviteCode ? buildInviteJoinUrl(inviteCode) : "";

  /* 淡霧頁首上的說明。標題「邀請成員」由釘住的標題列負責，這裡不再重複一次大標。 */
  const subHeader = (
    <YStack mb={16}>
      <Text color={v2.hazeSub} fontSize={13}>
        產生邀請碼給對方，加入後即可一起管理此帳戶
      </Text>
    </YStack>
  );

  return (
    <SkinV2Screen title="邀請成員" onBack={back} subHeader={subHeader}>
      <YStack className="gas-stagger-kids" gap="$4">
        {/* 邀請碼。虛線框保留（它是「這張是可以撕下來給人的東西」的暗示），
            但改成 V2 的圓角 24 與淡一階的線，不然 2px 實藍框在白卡林立的版面裡太搶。 */}
        <YStack
          bg={v2.card}
          br={24}
          px="$5"
          py="$5"
          ai="center"
          bw={1.5}
          bc={`${colors.primary}33`}
          style={{ borderStyle: "dashed" }}>
          <Text fontSize={13} fontWeight="700" color={v2.label} mb="$3">
            你的 6 位數邀請碼
          </Text>
          <XStack gap="$2" mb="$3">
            {cells.map((d, i) => (
              <View key={i} w={42} h={54} br={12} bg={v2.chipBg} ai="center" jc="center">
                <Text fontSize={25} fontWeight="800" color={inviteCode ? v2.primary : v2.sub} style={{ fontVariantNumeric: "tabular-nums" }}>
                  {d}
                </Text>
              </View>
            ))}
          </XStack>

          {/* QR Code（產生後顯示） */}
          {inviteCode && joinUrl ? (
            <YStack ai="center" gap="$2" mt="$1" mb="$3">
              <View bg={v2.card} p="$2" br={14} bw={1} bc={v2.divider}>
                <QRCodeSVG value={joinUrl} size={148} level="M" includeMargin={false} />
              </View>
              <Text fontSize={11.5} color={v2.sub}>
                對方掃描即可加入此帳戶
              </Text>
              {/* 小型「複製連結」：複製掃描加入用的 join URL */}
              <XStack
                ai="center"
                gap="$1"
                px="$2.5"
                py="$1.5"
                br={8}
                cursor="pointer"
                onPress={copyLink}
                pressStyle={{ opacity: 0.6 }}>
                {copiedLink ? <RiCheckLine size={13} color={colors.green} /> : <RiLinksLine size={13} color={v2.primary} />}
                <Text fontSize={12} fontWeight="700" color={copiedLink ? colors.greenText : v2.primary}>
                  {copiedLink ? "已複製連結" : "複製連結"}
                </Text>
              </XStack>
            </YStack>
          ) : null}

          <Text fontSize={11.5} color={v2.sub}>
            {expiryLabel}
          </Text>
        </YStack>

        {inviteCode ? (
          <YStack gap="$2.5">
            {/* 次要＋主要的雙鈕列，與瓦斯訂購流程的底欄同一款（SkinButton） */}
            <XStack gap="$2.5">
              <SkinButton
                flex={1}
                variant="secondary"
                label={copied ? "已複製" : "複製邀請碼"}
                icon={copied ? <RiCheckLine size={16} color={colors.green} /> : <RiFileCopyLine size={16} color={v2.primary} />}
                onPress={copy}
              />
              <SkinButton
                flex={1}
                label="分享"
                icon={<RiShareForwardLine size={16} color={colors.light} />}
                onPress={share}
              />
            </XStack>
            <XStack ai="center" jc="center" gap="$1.5" py="$2" cursor={loading ? "default" : "pointer"} opacity={loading ? 0.5 : 1} onPress={generate}>
              <RiRefreshLine size={14} color={v2.sub} />
              <Text fontSize={13} fontWeight="600" color={v2.sub}>
                {loading ? "產生中…" : "重新產生邀請碼"}
              </Text>
            </XStack>
          </YStack>
        ) : (
          <SkinButton label={loading ? "產生中…" : "產生邀請碼"} loading={loading} onPress={generate} />
        )}

        {/* 對方要怎麼加入 */}
        <SkinCard>
          <Text fontSize={14} fontWeight="800" color={v2.title} mb="$3">
            對方要怎麼加入？
          </Text>
          <YStack gap="$3">
            {HOW_TO_STEPS.map((step, i) => (
              <XStack key={i} gap="$2.5" ai="flex-start">
                <View w={22} h={22} br={11} bg={`${colors.primary}1F`} ai="center" jc="center" flexShrink={0}>
                  <Text fontSize={11} fontWeight="800" color={v2.primary}>
                    {i + 1}
                  </Text>
                </View>
                <Text f={1} fontSize={12.5} color={v2.body} lineHeight={20}>
                  {step}
                </Text>
              </XStack>
            ))}
          </YStack>
        </SkinCard>

        {/* 目前成員 */}
        <YStack>
          <SectionLabel title={`目前成員 (${members.length})`} />
          <ListContainer>
            {members.length === 0 ? (
              <Text fontSize={13} color={v2.sub} ta="center" py="$5">
                尚無成員資料
              </Text>
            ) : (
              members.map((m, i) => (
                <XStack key={i} ai="center" gap="$3" px="$4" py="$3" bbw={i === members.length - 1 ? 0 : 1} bbc={v2.divider}>
                  <View w={36} h={36} br={18} bg={`${m.color ?? colors.primary}25`} ai="center" jc="center" flexShrink={0}>
                    <Text fontSize={14} fontWeight="700" color={m.color ?? colors.primary}>
                      {m.customer_name.charAt(0)}
                    </Text>
                  </View>
                  <Text f={1} fontSize={14} fontWeight="600" color={v2.title}>
                    {m.customer_name}
                  </Text>
                </XStack>
              ))
            )}
          </ListContainer>
        </YStack>

        <InfoNote tone="primary">
          成員加入後可看到此帳戶的設定、地址、瓦斯點數，並能一起付款。
        </InfoNote>
      </YStack>
    </SkinV2Screen>
  );
};

export default InviteMember;
